/* eslint-disable @typescript-eslint/no-unused-expressions */
import { Survey, getMainAudience } from '@packages/cms-components';
import { promotionBannerDataFetcher } from '@packages/cms-components/src/modules/PromotionBanner/utils/fetch/promotionBannerDataFetcher';
import config, { getFullLocale } from '@packages/config';
import { createUrqlServerClient } from '@packages/gql';
import { BreadcrumbDataDocument } from '@packages/gql/generated/shopping/BreadcrumbDataDocument';
import { ProductDataDocument } from '@packages/gql/generated/shopping/ProductDataDocument';
import { type ProductDataQuery } from '@packages/gql/generated/shopping/graphql';
import { HydrateAtoms } from '@packages/modules/src/atoms';
import {
  ExtendedBreadcrumbs,
  KimRatingGridItem,
  ProductPageHead,
  ProductVariation,
  SeoList,
  getProductAndFallbackId,
} from '@packages/product';
import { DynamicYield, ErrorBoundary, Grid, searchParamsToObject, useI18n } from '@packages/shared';
import { useTracking } from '@packages/tracking';
import { getEcState, logger, useConfig } from '@packages/utilities';
import { Provider as JotaiProvider } from 'jotai';
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import type { ParsedUrlQuery } from 'querystring';
import { useEffect, useMemo } from 'react';
import { useQuery } from 'urql';

/* GraphQL */ `
  query ProductData(
    $categoryId: ID!
    $skipBreadcrumb: Boolean! = false
    $skipCliplister: Boolean! = false
    $skipAplus: Boolean! = false
    $locale: String!
    $lang: String!
    $productId: String!
    $fallbackId: String!
  ) {
    product(id: $productId, fallbackId: $fallbackId, locale: $locale) {
      akl
      sku
      slug
      defaultCategory
      name
      seo(locale: $locale) {
        breadcrumb
      }
      # ean is not used by any component but we need it for loadbee.
      # If ean is removed loadbee returns null. Maybe mercurius fixes this issue, then ean can be removed again.
      ean
      slug
      ...CompactProductFragment
      ...KimRatingGridItem
      ...ProductVariationFragment
      ...ProductSeo
      ...SeoListFragment
      seoRating(locale: $locale) {
        ...RatingSeoResultFragment
      }
    }
    breadcrumb(category: $categoryId, locale: $locale) @skip(if: $skipBreadcrumb) {
      ...BreadcrumbsFragment
      ...TrackingBreadcrumbsFragment
    }
  }
`;

/* GraphQL */ `
  query BreadcrumbData($categoryId: ID!, $locale: String!) {
    breadcrumb(category: $categoryId, locale: $locale) {
      ...BreadcrumbsFragment
      ...TrackingBreadcrumbsFragment
    }
  }
`;

const getDVOrigin = (query: ParsedUrlQuery) => ({
  searchOrigin: query['nav-q'] ? (query['nav-q'] as string) : '',
  categoryOrigin: query['nav-c'] ? (query['nav-c'] as string) : '',
});

const Detailview = ({ referer }: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { replace } = useRouter();
  const searchParams = useSearchParams();

  const sku = searchParams?.get('sku') || '';
  const params = useParams<{ slug: string; akl?: string }>();

  const { language, defaultLanguage, locale, localizeUrl } = useI18n();
  const { searchOrigin, categoryOrigin } = getDVOrigin(searchParamsToObject(searchParams));

  const {
    dynamicYieldEnabled,
    product: {
      aplus: { enabled: aplusEnabled },
      cliplister: { enabled: cliplisterEnabled },
    },
  } = useConfig();

  const dispatchGTMEvent = useTracking();

  useEffect(() => {
    if (categoryOrigin && categoryOrigin.length > 0) {
      dispatchGTMEvent({
        categoryId: categoryOrigin,
        categoryIds: [],
        categoryPath: [],
        event: 'categoryData',
        eventCategory: 'data',
      });
    }
  }, [categoryOrigin, dispatchGTMEvent]);

  const context = useMemo(() => ({ suspense: false }), []);

  const [{ data, fetching }] = useQuery({
    query: ProductDataDocument,
    variables: {
      ...getProductAndFallbackId(params?.akl ?? params?.slug, sku),
      locale,
      lang: language || defaultLanguage,
      skipBreadcrumb: categoryOrigin.length === 0,
      categoryId: categoryOrigin,
      skipCliplister: !cliplisterEnabled,
      skipAplus: !aplusEnabled,
    },
    context,
  });

  const { product } = { ...data };

  // if sku in URL differs to the product sku it has to be the default product -> redirect to default sku [NJS-979]
  // has to be client side because of seo needs
  useEffect(() => {
    if (
      (product && !params?.akl) ||
      (product && sku && !Array.isArray(sku) && !product.sku.startsWith(sku))
    ) {
      let searchParamsForReplace = new URLSearchParams(searchParams?.toString());
      // check if there is no sku in search params and if add it
      if (!searchParamsForReplace.has('sku')) {
        // sku has to be the first
        searchParamsForReplace = new URLSearchParams({
          sku: product.sku,
        });
        // then add all the others
        // must be added this way because ...Object.entries(searchParams) would remove multiple params with the same key
        searchParams?.forEach((value, key) => {
          searchParamsForReplace.append(key, value);
        });
      }

      replace(
        localizeUrl(
          `/p/${product.slug}/${product.akl}?${searchParamsForReplace.toString()}`,
          language,
        ),
        { scroll: false },
      );
    }
  }, [product, sku, params?.akl, replace, localizeUrl, searchParams, language]);

  let breadcrumb = data?.breadcrumb;

  const [{ data: fallbackBreadcrumb }] = useQuery({
    query: BreadcrumbDataDocument,
    variables: {
      locale,
      categoryId: product?.seo?.breadcrumb || product?.defaultCategory || '',
    },
    pause: categoryOrigin.length > 0 || searchOrigin.length > 0,
    context,
  });

  if (!breadcrumb && fallbackBreadcrumb) {
    breadcrumb = fallbackBreadcrumb.breadcrumb;
  }

  if (!product || fetching) return null;

  return (
    <JotaiProvider key={product.akl}>
      <HydrateAtoms>
        <Grid
          container
          rowSpacing={0.5}
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
        >
          <ProductPageHead data={product} />
          {dynamicYieldEnabled && (
            <>
              <DynamicYield dataDyId="dv_notification-campaign" />
              <DynamicYield dataDyId="dv-mkz-banner-popup" />
            </>
          )}
          <Grid item xs={12}>
            <ExtendedBreadcrumbs breadCrumbData={breadcrumb} />
          </Grid>

          <ProductVariation
            searchOrigin={searchOrigin}
            breadcrumbData={breadcrumb}
            productData={product}
            seoListSlot={
              <Grid item xs={12}>
                <SeoList productData={product} />
              </Grid>
            }
            kimRatingSlot={<KimRatingGridItem productData={product} referer={referer} />}
          />

          <ErrorBoundary>
            <Survey />
          </ErrorBoundary>
        </Grid>
      </HydrateAtoms>
    </JotaiProvider>
  );
};

export const getServerSideProps = (async (context) => {
  const { res, query, req } = context;
  // set response cache-control header (browser cache is unset in development)
  res.setHeader('Cache-Control', 'public, s-maxage=10, stale-while-revalidate=59');

  const { akl, sku, slug, lang } = query;
  const language = lang as string;
  const isBot: boolean = req.headers['x-ua-bot'] !== undefined;
  const { categoryOrigin, searchOrigin } = getDVOrigin(query);

  let productData: ProductDataQuery['product'] = null;
  let breadcrumbData: ProductDataQuery['breadcrumb'] = null;
  const locale = getFullLocale(language);

  const { ssrCache, ssrClient } = createUrqlServerClient();
  const audience = req.cookies ? getMainAudience(getEcState(req.cookies)) : 'anonymous';
  const promotionBannerCacheKey = `promotionBannerData-${locale.substring(0, 2)}-${audience}`;

  const [fetchedProductData, promoData] = await Promise.allSettled([
    // get product data
    ssrClient
      .query(ProductDataDocument, {
        ...getProductAndFallbackId(akl ?? slug, sku),
        locale,
        lang: language || config.i18n.defaultLocale.split('-')[0]!,
        skipBreadcrumb: categoryOrigin.length === 0,
        categoryId: categoryOrigin,
        skipCliplister: !config.product.cliplister.enabled,
        skipAplus: !config.product.aplus.enabled,
      })
      .toPromise(),
    promotionBannerDataFetcher(
      { ...query, ...{ shopId: categoryOrigin } },
      req.cookies,
      context,
      locale,
    ),
  ]);

  if (
    fetchedProductData.status === 'rejected' ||
    fetchedProductData.value.error ||
    fetchedProductData.value.data?.product === null
  ) {
    logger.error(
      fetchedProductData.status === 'rejected'
        ? fetchedProductData.reason
        : fetchedProductData.value.error,
      `Error during fetching product (akl: ${akl}, sku: ${sku}, slug: ${slug}), user is bot (${isBot})`,
    );

    if (
      fetchedProductData.status === 'rejected' ||
      fetchedProductData.value.data?.product === null
    ) {
      return {
        notFound: true,
      };
    }
  }

  if (promoData.status === 'rejected') {
    logger.error(
      { description: 'Promotion Banner Data Fetch (Product-Page)' },
      `Could not fetch data for ID ${categoryOrigin}`,
    );
  }

  if (fetchedProductData.status === 'fulfilled' && fetchedProductData.value.data?.product) {
    productData = fetchedProductData.value.data.product;
    breadcrumbData = fetchedProductData.value.data.breadcrumb;
    if (
      !breadcrumbData &&
      productData.defaultCategory &&
      searchOrigin.length === 0 &&
      categoryOrigin.length === 0
    ) {
      await ssrClient
        .query(BreadcrumbDataDocument, {
          categoryId: productData.seo?.breadcrumb || productData.defaultCategory,
          locale,
        })
        .toPromise();
    }
  }

  return {
    props: {
      urqlState: productData ? ssrCache.extractData() : null,
      fallback: {
        [promotionBannerCacheKey]: promoData.status === 'fulfilled' ? promoData.value : null,
      },
      // referer needed for KimRating -> use headers from next/headers when switching to app folder
      ...(req?.headers?.referer && { referer: req.headers.referer }),
    },
  };
}) satisfies GetServerSideProps<{ referer?: string }>;

// eslint-disable-next-line import/no-default-export
export default Detailview;
